/**
 * product-image-upload.js
 * Eben & Ink — Custom Print Upload Handler
 *
 * Handles file selection, validation, upload to Cloudflare R2 via Worker,
 * and injects the returned URL as a line item property on the buy form.
 *
 * Line item property injected:
 *   - Custom Image → original R2 URL (for Kittl/Gemini processing)
 */

class ProductImageUpload {
  constructor(container) {
    this.container  = container;
    this.workerUrl  = container.dataset.workerUrl;
    this.elementId  = container.dataset.elementId;
    this.formId     = container.dataset.formId;

    // Elements
    this.fileInput  = container.querySelector(`#${this.elementId}-file`);
    this.urlInput   = container.querySelector(`#${this.elementId}-url`);
    this.previewWrap = container.querySelector('.image-upload-block__preview');
    this.previewImg  = container.querySelector(`#${this.elementId}-preview`);
    this.removeBtn   = container.querySelector('.image-upload-block__remove');
    this.message     = container.querySelector('.image-upload-block__message');
    this.progress    = container.querySelector('.image-upload-block__progress');
    this.progressBar = container.querySelector('.image-upload-block__progress-bar');
    this.addToCartBtn = null;

    if (!this.workerUrl) {
      console.warn('[ImageUpload] No Worker URL configured in block settings.');
      return;
    }

    container._uploadInstance = this;

    this._bindEvents();
    this._resolveAddToCartButton();
    this._hookFormSubmit();
  }

  // ── Event binding ─────────────────────────────────────────────────────────

  _bindEvents() {
    this.fileInput.addEventListener('change', (e) => {
      const file = e.target.files?.[0];
      if (file) this._handleFile(file);
    });

    this.removeBtn.addEventListener('click', () => this._reset());

    this.container.addEventListener('dragover', (e) => {
      e.preventDefault();
      this.container.dataset.dragOver = 'true';
    });

    this.container.addEventListener('dragleave', () => {
      delete this.container.dataset.dragOver;
    });

    this.container.addEventListener('drop', (e) => {
      e.preventDefault();
      delete this.container.dataset.dragOver;
      const file = e.dataTransfer.files?.[0];
      if (file) this._handleFile(file);
    });
  }

  _resolveAddToCartButton() {
    const form = document.getElementById(this.formId);
    if (form) {
      this.addToCartBtn = form.querySelector('[name="add"]');
    }
  }

  _hookFormSubmit() {
    const waitForForm = setInterval(() => {
      const form = document.getElementById(this.formId);
      if (form) {
        clearInterval(waitForForm);

        // Block submit if no image uploaded
        form.addEventListener('submit', (e) => {
          if (!this.urlInput.value) {
            e.preventDefault();
            e.stopImmediatePropagation();
            this._showMessage('Please upload your image before adding to cart.', 'error');
            this.fileInput.focus();
          }
        }, true);

        // Clear fields after successful cart add
        form.addEventListener('submit', () => {
          if (this.urlInput.value) {
            setTimeout(() => this._reset(), 1500);
          }
        });
      }
    }, 100);
    setTimeout(() => clearInterval(waitForForm), 10000);
  }

  // ── File handling ─────────────────────────────────────────────────────────

  _handleFile(file) {
    const allowed = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowed.includes(file.type)) {
      this._showMessage('Please upload a JPG or PNG image.', 'error');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      this._showMessage('Image too large. Maximum 50MB.', 'error');
      return;
    }

    if (file.size < 1.5 * 1024 * 1024) {
      this._showMessage('Image too small. Please upload a higher resolution photo (minimum 1.5MB).', 'error');
      return;
    }

    this._showPreview(file);
    this._upload(file);
  }

  _showPreview(file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      this.previewImg.src = e.target.result;
      this.previewWrap.classList.remove('hidden');
    };
    reader.readAsDataURL(file);
  }

  // ── Upload ────────────────────────────────────────────────────────────────

  async _upload(file) {
    this._showProgress(true);
    this._showMessage('Uploading your photo…', 'info');
    this._setAddToCartDisabled(true);
    this._animateProgress();

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(this.workerUrl, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Upload failed');
      }

      this.urlInput.value = data.url;

      this._showProgress(false);
      this._showMessage('✓ Photo uploaded successfully!', 'success');
      this._setAddToCartDisabled(false);

    } catch (err) {
      console.error('[ImageUpload] Upload error:', err);
      this._showProgress(false);
      this._showMessage(err.message || 'Upload failed. Please try again.', 'error');
      this._reset(false);
      this._setAddToCartDisabled(false);
    }
  }

  // ── UI helpers ────────────────────────────────────────────────────────────

  _animateProgress() {
    let width = 0;
    this._progressInterval = setInterval(() => {
      if (width < 90) {
        width += Math.random() * 8;
        this.progressBar.style.width = `${Math.min(width, 90)}%`;
      } else {
        clearInterval(this._progressInterval);
      }
    }, 200);
  }

  _showProgress(show) {
    if (!show) {
      clearInterval(this._progressInterval);
      this.progressBar.style.width = '100%';
      setTimeout(() => {
        this.progress.classList.add('hidden');
        this.progressBar.style.width = '0%';
      }, 400);
    } else {
      this.progress.classList.remove('hidden');
    }
  }

  _showMessage(text, type = 'info') {
    this.message.textContent = text;
    this.message.className   = 'image-upload-block__message';
    if (type === 'error')   this.message.classList.add('image-upload-block__message--error');
    if (type === 'success') this.message.classList.add('image-upload-block__message--success');
    this.message.classList.remove('hidden');
  }

  _setAddToCartDisabled(disabled) {
    if (!this.addToCartBtn) this._resolveAddToCartButton();
    if (this.addToCartBtn) this.addToCartBtn.disabled = disabled;
  }

  _reset(clearPreview = true) {
    this.urlInput.value  = '';
    this.fileInput.value = '';

    if (clearPreview) {
      this.previewWrap.classList.add('hidden');
      this.previewImg.src = '';
    }

    this.message.classList.add('hidden');
    this.message.textContent = '';
  }
}

// ── Lightbox ──────────────────────────────────────────────────────────────────

function initLightbox() {
  if (document.querySelector('.ei-lightbox')) return;

  const lb = document.createElement('div');
  lb.className = 'ei-lightbox';
  lb.innerHTML = `
    <button class="ei-lightbox__close" aria-label="Close">✕</button>
    <img class="ei-lightbox__img" src="" alt="Preview">
  `;
  document.body.appendChild(lb);

  const lbImg   = lb.querySelector('.ei-lightbox__img');
  const lbClose = lb.querySelector('.ei-lightbox__close');

  function open(src) {
    lbImg.src = src;
    lb.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function close() {
    lb.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(() => { lbImg.src = ''; }, 200);
  }

  lb.addEventListener('click', (e) => {
    if (e.target === lb || e.target === lbClose) close();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });

  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-lightbox]');
    if (!el) return;
    e.preventDefault();
    const src = el.src || el.href;
    if (src) open(src);
  });
}

// ── Init ──────────────────────────────────────────────────────────────────────

function initUploadBlocks() {
  document.querySelectorAll('.image-upload-block[data-worker-url]').forEach((container) => {
    if (!container.dataset.uploadInit) {
      container.dataset.uploadInit = 'true';
      new ProductImageUpload(container);
    }
  });
  initLightbox();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUploadBlocks);
} else {
  initUploadBlocks();
}

document.addEventListener('shopify:section:load', initUploadBlocks);